// src/features/workflows/ClosureStartDrawer.jsx
//
// W3: CLOSURE-WF-START-STEPPER — Manual 3-step launch drawer.
//
// Opens as a right-side 500px overlay from the Actions home when the user
// clicks "+ Start action". No colored edge stripe (house rule).
//
// Step 1 — Choose playbook: Office Closure / Relocation card + library note;
//           Continue always enabled.
// Step 2 — Add context: Source office, Target office, Effective date, Owner,
//           Notes; WorkplaceOS callout.
// Step 3 — Review & launch: read-back rows + "Appears in Runs in motion"
//           callout; Launch button posts createWorkflowRun.
//
// On 409 (manual path): inline dedup notice with a "View run" link.
// On 409 (reactive path): if onDedupNavigate is provided, it is called instead
//   of showing the inline notice so the user is taken directly to the run.
// On success: onLaunched(runId).

import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Building2, Check, AlertTriangle } from "lucide-react";
import { DrawerCloseButton } from "../../v2/components/DrawerCloseButton.jsx";
import { T } from "../../app/tokens.js";
import { APP_NAME } from "../../constants/branding.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { getWorkflowTemplates, createWorkflowRun } from "../../data/workflowsClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";

const CLOSURE_TEMPLATE_NAME = "Office Closure";

const STEPS = [
  { key: "playbook", label: "Choose playbook" },
  { key: "context",  label: "Add context"     },
  { key: "review",   label: "Review & launch" },
];

// ─── StepBar ─────────────────────────────────────────────────────────────────

function StepBar({ stepIndex }) {
  return (
    <div
      data-testid="closure-drawer-step-bar"
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}
    >
      {STEPS.map((s, i) => {
        const done   = i < stepIndex;
        const active = i === stepIndex;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 22, height: 22, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: T.fsXxs, fontWeight: T.fwBold,
                  background: done ? T.green : active ? T.blue : T.bg,
                  color: done || active ? T.white : T.mid,
                  border: active ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                  flexShrink: 0,
                }}
              >
                {done ? <Check size={11} /> : i + 1}
              </div>
              <span style={{ fontSize: T.fsXs, fontWeight: active ? T.fwBold : T.fwRegular, color: active ? T.dark : T.mid, whiteSpace: "nowrap" }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: T.border, minWidth: 12 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── fieldStyle ──────────────────────────────────────────────────────────────

function fieldStyle() {
  return {
    width: "100%", boxSizing: "border-box",
    padding: "8px 10px",
    fontSize: T.fsSm5,
    border: `1px solid ${T.border}`,
    borderRadius: 7,
    color: T.dark,
    background: T.white,
  };
}

// ─── Callout ─────────────────────────────────────────────────────────────────

function Callout({ children }) {
  return (
    <div style={{
      background: T.blueSoft,
      border: `1px solid ${T.blue}`,
      borderRadius: 9, padding: "10px 13px",
      fontSize: T.fsXs, color: T.blue, fontWeight: T.fwSemibold,
      marginTop: 16,
    }}>
      {children}
    </div>
  );
}

// ─── Step 1: Playbook card ────────────────────────────────────────────────────

function StepPlaybook() {
  return (
    <div data-testid="closure-step-playbook">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Choose a playbook
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Select the action type to launch.
      </div>

      {/* Single playbook card — Office Closure / Relocation */}
      <div
        data-testid="closure-playbook-card"
        style={{
          border: `2px solid ${T.blue}`,
          background: T.blueSoft,
          borderRadius: 10, padding: "14px 16px",
          display: "flex", alignItems: "flex-start", gap: 12,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: T.blue, display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Building2 size={18} color={T.white} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
            Office Closure / Relocation
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              "3 phases",
              "8 checks",
              "5 connected records",
            ].map((chip) => (
              <span
                key={chip}
                style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.blue,
                  background: T.white, border: `1px solid ${T.blue}`,
                  borderRadius: 6, padding: "2px 10px",
                }}
              >
                {chip}
              </span>
            ))}
          </div>
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 8, lineHeight: 1.5 }}>
            Tracks vacate, capacity zeroing, headcount remap, lease status, and
            disposition - with Workday notification and a final reconcile step.
          </div>
        </div>
        <Check size={16} color={T.blue} style={{ flexShrink: 0, marginTop: 2 }} />
      </div>

      {/* Library note */}
      <div style={{
        marginTop: 14,
        fontSize: T.fsXs, color: T.mid,
        padding: "10px 13px",
        background: T.card, border: `1px solid ${T.border}`, borderRadius: 9,
      }}>
        <strong style={{ color: T.dark }}>Playbook library.</strong>{" "}
        More playbooks - lease renewals, space builds, and headcount-driven rebalances - are
        added here as they are formalised.
      </div>
    </div>
  );
}

// ─── Step 2: Context form ─────────────────────────────────────────────────────

function StepContext({ form, onChange, locations, users }) {
  const locLabel = (loc) => loc.locationAlias || loc.name || loc.id;

  return (
    <div data-testid="closure-step-context">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Add context
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Tell {APP_NAME} what's moving, where, and when.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {/* Source office */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Source office *
          <select
            data-testid="closure-input-source"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.sourceLocationId}
            onChange={(e) => onChange("sourceLocationId", e.target.value)}
          >
            <option value="">Select source office…</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{locLabel(loc)}</option>
            ))}
          </select>
        </label>

        {/* Target office */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Target office
          <select
            data-testid="closure-input-target"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.targetLocationId}
            onChange={(e) => onChange("targetLocationId", e.target.value)}
          >
            <option value="">Select target office…</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{locLabel(loc)}</option>
            ))}
          </select>
        </label>

        {/* Effective date */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Effective date
          <input
            type="date"
            data-testid="closure-input-date"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.closeDate}
            onChange={(e) => onChange("closeDate", e.target.value)}
          />
        </label>

        {/* Owner */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Owner
          <select
            data-testid="closure-input-owner"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.ownerName}
            onChange={(e) => onChange("ownerName", e.target.value)}
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.displayName}>{u.displayName}</option>
            ))}
          </select>
        </label>

        {/* Notes */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Notes
          <textarea
            data-testid="closure-input-notes"
            style={{ ...fieldStyle(), marginTop: 4, minHeight: 72, resize: "vertical", fontFamily: "inherit" }}
            value={form.reason}
            onChange={(e) => onChange("reason", e.target.value)}
            placeholder="Reason for closure, stakeholders, or other context…"
          />
        </label>
      </div>

      <Callout>
        {APP_NAME} assembles the run from live records - stage, capacity,
        lease status, disposition, and headcount - so fact checks reflect
        real state from the moment you launch.
      </Callout>
    </div>
  );
}

// ─── Step 3: Review ───────────────────────────────────────────────────────────

function StepReview({ form, locations, dupNotice, onViewRun }) {
  const locLabel = (id) => {
    const loc = locations.find((l) => l.id === id);
    return loc ? (loc.locationAlias || loc.name || loc.id) : id || "—";
  };

  const rows = [
    ["Source",    locLabel(form.sourceLocationId)],
    ["Target",    form.targetLocationId ? locLabel(form.targetLocationId) : "—"],
    ["Effective", form.closeDate || "—"],
    ["Structure", "Office Closure / Relocation"],
    ["Owner",     form.ownerName || "Unassigned"],
  ];

  return (
    <div data-testid="closure-step-review">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Review & launch
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Confirm the details below before launching the run.
      </div>

      {/* Dedup notice */}
      {dupNotice && (
        <div
          data-testid="closure-dedup-notice"
          style={{
            background: T.utilAmber,
            border: `1px solid ${T.negotiateOrange}`,
            borderRadius: 9, padding: "12px 14px",
            marginBottom: 14,
            display: "flex", alignItems: "flex-start", gap: 10,
          }}
        >
          <AlertTriangle size={16} color={T.negotiateOrange} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginBottom: 3 }}>
              Run already in motion
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 6 }}>
              An open closure run already exists for this source office.
            </div>
            <button
              type="button"
              data-testid="closure-dedup-view-run"
              onClick={() => onViewRun(dupNotice.existingId)}
              style={{
                background: "transparent", border: `1px solid ${T.negotiateOrange}`,
                borderRadius: 7, padding: "5px 12px",
                fontSize: T.fsXs, fontWeight: T.fwBold,
                color: T.negotiateOrange, cursor: "pointer",
              }}
            >
              View run →
            </button>
          </div>
        </div>
      )}

      {/* Read-back rows */}
      <div style={{
        border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden",
        marginBottom: 14,
      }}>
        {rows.map(([label, value], i) => (
          <div
            key={label}
            style={{
              display: "flex", justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none",
            }}
          >
            <span style={{ fontSize: T.fsXs, color: T.mid }}>{label}</span>
            <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>{value}</span>
          </div>
        ))}
        {form.reason && (
          <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 3 }}>Notes</div>
            <div style={{ fontSize: T.fsSm5, color: T.dark }}>{form.reason}</div>
          </div>
        )}
      </div>

      {/* "Appears in Runs in motion" callout */}
      <div style={{
        background: T.greenSoft,
        border: `1px solid ${T.green}`,
        borderRadius: 9, padding: "10px 13px",
        fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold,
      }}>
        <span style={{ color: T.green }}>✓</span>{" "}
        Once launched, this run appears in{" "}
        <strong>Actions in motion</strong> and auto-evaluates all 8 fact checks
        against live records.
      </div>
    </div>
  );
}

// ─── ClosureStartDrawer ───────────────────────────────────────────────────────
//
// W4 additions:
//   initialSourceLocationId — pre-fill the source office on step 2
//   initialCloseDate        — pre-fill the effective date on step 2
//   initialOwnerName        — pre-fill the owner on step 2
//   launchedFrom            — context.launchedFrom written to the run; defaults to "manual"

export function ClosureStartDrawer({
  canEdit,
  onClose,
  onLaunched,
  initialSourceLocationId = "",
  initialCloseDate        = "",
  initialOwnerName        = "",
  launchedFrom            = "manual",
  onDedupNavigate         = null,
}) {
  const [stepIndex,    setStepIndex]    = useState(0);
  const [locations,    setLocations]    = useState([]);
  const [users,        setUsers]        = useState([]);
  const [templateId,   setTemplateId]   = useState(null);
  const [templateErr,  setTemplateErr]  = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState(null);
  const [dupNotice,    setDupNotice]    = useState(null);

  const [form, setForm] = useState({
    sourceLocationId: initialSourceLocationId,
    targetLocationId: "",
    closeDate:        initialCloseDate,
    ownerName:        initialOwnerName,
    reason:           "",
  });

  const onChange = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setDupNotice(null);
  }, []);

  // Load data on mount
  useEffect(() => {
    let cancelled = false;

    // Templates
    getWorkflowTemplates()
      .then((list) => {
        if (cancelled) return;
        const match = list.find((t) => t.name === CLOSURE_TEMPLATE_NAME);
        if (match) {
          setTemplateId(match.id);
        } else {
          setTemplateErr(`No "${CLOSURE_TEMPLATE_NAME}" template found in the library. Create it first.`);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error("ClosureStartDrawer: failed to load templates", err);
          setTemplateErr("Could not load templates. Please try again.");
        }
      });

    // Locations
    getLocations()
      .then((list) => { if (!cancelled) setLocations(Array.isArray(list) ? list : []); })
      .catch((err) => { log.error("ClosureStartDrawer: failed to load locations", err); });

    // Users (plan users endpoint)
    fetch("/api/plan/users")
      .then((r) => r.ok ? r.json() : [])
      .then((list) => { if (!cancelled) setUsers(Array.isArray(list) ? list : []); })
      .catch((err) => { log.error("ClosureStartDrawer: failed to load users", err); });

    return () => { cancelled = true; };
  }, []);

  const currentStep = STEPS[stepIndex].key;

  const goNext = useCallback(() => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1)), []);
  const goBack = useCallback(() => {
    setDupNotice(null);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  async function handleLaunch() {
    if (!templateId) {
      setError(new Error(templateErr || "Template not available."));
      return;
    }
    setSubmitting(true);
    setError(null);
    setDupNotice(null);
    try {
      const run = await createWorkflowRun({
        templateId,
        name: buildRunName(form, locations),
        ownerName:        form.ownerName || null,
        reason:           form.reason    || null,
        sourceLocationId: form.sourceLocationId || null,
        targetLocationId: form.targetLocationId || null,
        closeDate:        form.closeDate || null,
        context: { launchedFrom },
      });
      showToast("Action launched.", { type: "success" });
      onLaunched(run.id);
    } catch (err) {
      if (err?.status === 409 || err?.statusCode === 409) {
        const body = err?.body ?? err?.data ?? {};
        const existingId = body.existingId ?? null;
        if (onDedupNavigate) {
          // Reactive path: navigate silently to the existing run; no inline notice.
          onDedupNavigate(existingId);
        } else {
          setDupNotice({ existingId });
        }
      } else {
        log.error("ClosureStartDrawer: failed to launch run", err);
        setError(err);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleViewRun(id) {
    onClose();
    onLaunched(id);
  }

  // Backdrop click closes
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="closure-drawer-backdrop"
        onClick={handleBackdropClick}
        style={{ position: "fixed", inset: 0, zIndex: 49, background: "rgba(10,25,47,0.28)" }}
      />

      {/* Drawer panel */}
      <div
        data-testid="closure-start-drawer"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(500px, 96vw)",
          background: T.white, borderLeft: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column", zIndex: 50,
          boxShadow: "-4px 0 24px rgba(18,39,70,0.10)",
        }}
      >
        <DrawerCloseButton onClick={onClose} testId="closure-drawer-close" />
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ marginBottom: 16, paddingRight: 36 }}>
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark }}>
              Start an action
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>
              Office Closure / Relocation · manual launch
            </div>
          </div>
          <StepBar stepIndex={stepIndex} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 16px" }}>
          {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

          {templateErr && currentStep !== "playbook" && (
            <div style={{
              background: T.redSoft, border: `1px solid ${T.red}`,
              borderRadius: 9, padding: "10px 13px",
              fontSize: T.fsXs, color: T.red, fontWeight: T.fwSemibold,
              marginBottom: 14,
            }}>
              {templateErr}
            </div>
          )}

          {currentStep === "playbook" && <StepPlaybook />}
          {currentStep === "context" && (
            <StepContext
              form={form}
              onChange={onChange}
              locations={locations}
              users={users}
            />
          )}
          {currentStep === "review" && (
            <StepReview
              form={form}
              locations={locations}
              dupNotice={dupNotice}
              onViewRun={handleViewRun}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 20px", borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
        }}>
          {/* Back / Cancel */}
          <button
            type="button"
            data-testid="closure-drawer-back"
            onClick={stepIndex === 0 ? onClose : goBack}
            style={{
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: 8, padding: "8px 16px",
              fontSize: T.fsSm5, color: T.dark, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <ArrowLeft size={14} /> {stepIndex === 0 ? "Cancel" : "Back"}
          </button>

          {/* Continue / Launch */}
          {currentStep === "review" ? (
            canEdit ? (
              <button
                type="button"
                data-testid="closure-drawer-launch"
                onClick={handleLaunch}
                disabled={submitting || !!templateErr}
                style={{
                  background: T.blue, color: T.white, border: "none",
                  borderRadius: 8, padding: "8px 20px",
                  fontSize: T.fsSm5, fontWeight: T.fwBold,
                  cursor: submitting || !!templateErr ? "default" : "pointer",
                  opacity: submitting || !!templateErr ? 0.7 : 1,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {submitting ? <Loader2 size={14} className="spin" /> : null}
                Launch
              </button>
            ) : null
          ) : (
            <button
              type="button"
              data-testid="closure-drawer-continue"
              onClick={goNext}
              style={{
                background: T.blue, color: T.white, border: "none",
                borderRadius: 8, padding: "8px 20px",
                fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              Continue <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildRunName(form, locations) {
  const srcLoc = locations.find((l) => l.id === form.sourceLocationId);
  const srcLabel = srcLoc ? (srcLoc.locationAlias || srcLoc.name || srcLoc.id) : form.sourceLocationId;
  if (srcLabel) return `Office Closure - ${srcLabel}`;
  return "Office Closure / Relocation";
}
